import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  ContextualFlatTeamSettingGroupById,
  type ContextualFlatTeamSettingGroupId,
} from "./groups";

type ContextualSettingInput = SettingRegistryMetadataInput;

function setting(metadata: ContextualSettingInput): ContextualSettingInput {
  return metadata;
}

function contextualGroup<
  const Group extends ContextualFlatTeamSettingGroupId,
  const T extends Record<string, ContextualSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const ContextualRegistry = {
  ...contextualGroup("contextual.general", {
    "contextual/enabled": setting({
      label: "Contextual enabled",
      description:
        "Whether the Contextual integration runs for this team. Off means the storefront script tag is not installed.",
      synonyms: ["contextual", "contextual enabled"],
    }),
    "contextual/visible": setting({
      label: "Contextual visible",
      description:
        "Whether Contextual is surfaced in the merchant app, separate from whether it runs.",
      synonyms: ["contextual visible"],
    }),
    "contextual/contextualMerchantId": setting({
      label: "Contextual merchant id",
      description:
        "The merchant identifier Contextual issued for this team, recorded during onboarding.",
      synonyms: ["contextual merchant id"],
    }),
    "contextual/ctxDomain": setting({
      label: "Contextual subdomain",
      description:
        "The subdomain Contextual serves from. Set the moment a merchant registers, which is well before the CNAME resolves, so it does not by itself mean the host is live.",
      synonyms: ["contextual domain", "ctx domain"],
    }),
    "contextual/cloudflareHostnameId": setting({
      label: "Cloudflare hostname id",
      description:
        "The Cloudflare custom-hostname record backing the Contextual subdomain.",
      synonyms: ["cloudflare hostname"],
    }),
    "contextual/onboardingCompleted": setting({
      label: "Contextual onboarding completed",
      description: "Whether the merchant finished the Contextual setup flow.",
      synonyms: ["contextual onboarding"],
    }),
    "contextual/gtmContainerId": setting({
      label: "Google Tag Manager container id",
      description:
        "The GTM container Contextual is installed through, where the merchant uses one.",
      synonyms: ["gtm container", "google tag manager"],
    }),
    "contextual/pixelVerified": setting({
      label: "Contextual pixel verified",
      description:
        "Whether Redo has confirmed the Contextual pixel is firing on the storefront.",
      synonyms: ["contextual pixel"],
    }),
    "contextual/subdomainActive": setting({
      label: "Contextual subdomain active",
      description:
        "Whether the Contextual subdomain actually resolves. The storefront loader keys on this rather than on the subdomain being set, because pointing the loader at a host that does not resolve yet leaves no SDK and no way to fall back.",
      synonyms: ["contextual subdomain active"],
    }),
  }),
};

export type ContextualFlatTeamSettingKey = keyof typeof ContextualRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ContextualFlatTeamSettingKeys = Object.keys(
  ContextualRegistry,
) as ContextualFlatTeamSettingKey[];

export const ContextualFlatTeamSettingCatalogDefinitions =
  ContextualFlatTeamSettingKeys.map((key) => {
    const definition = ContextualRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: ContextualFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: {
        adapter: "teamSettings",
        key,
      } satisfies TeamSettingsSettingValueSource<ContextualFlatTeamSettingKey>,
    };
  }) satisfies readonly SettingDefinition<
    ContextualFlatTeamSettingKey,
    ContextualFlatTeamSettingGroupId
  >[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const ContextualFlatTeamSettingMetadataByKey = Object.fromEntries(
  ContextualFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<ContextualFlatTeamSettingKey, SettingMetadata>;
